import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Container, Typography, Grid, Card, CardContent, CardMedia, Chip, Button,
  TextField, MenuItem, InputAdornment, Paper, Skeleton, Pagination, Stack,
  FormControl, InputLabel, Select, Slider, Checkbox, FormControlLabel, Drawer,
  IconButton, useMediaQuery, useTheme,
} from '@mui/material';
import {
  Search, LocationOn, Wifi, Bolt, LocalParking, Water, FilterList,
  Close, Star, Bed, Group, School, MyLocation, Security, AcUnit,
  ElectricalServices, Kitchen, Wash, Receipt,
} from '@mui/icons-material';
import { propertiesAPI, ghanaAPI } from '../../api/client';
import { decimalOnly } from '../../utils/inputHelpers';

function PropertyCard({ property }) {
  const navigate = useNavigate();
  const primaryImage = property.images?.find(img => img.is_primary) || property.images?.[0];

  return (
    <Card
      sx={{ height: '100%', cursor: 'pointer', transition: 'all 0.2s', '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 8px 30px rgba(0,0,0,0.12)' } }}
      onClick={() => navigate(`/properties/${property.id}`)}
    >
      <CardMedia
        component="div"
        sx={{ height: 200, bgcolor: 'grey.200', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}
      >
        {primaryImage ? (
          <Box component="img" src={primaryImage.image_url} alt={property.title} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <Bed sx={{ fontSize: 60, color: 'grey.400' }} />
        )}
        <Chip
          label={property.location_type === 'on_campus' ? 'On Campus' : 'Off Campus'}
          size="small"
          color={property.location_type === 'on_campus' ? 'primary' : 'secondary'}
          sx={{ position: 'absolute', top: 8, left: 8 }}
        />
        {property.allows_pairing && (
          <Chip label="Pairing Available" size="small" icon={<Group />} sx={{ position: 'absolute', top: 8, right: 8, bgcolor: 'rgba(255,255,255,0.9)' }} />
        )}
      </CardMedia>
      <CardContent>
        <Typography variant="h6" fontWeight={600} noWrap>{property.title}</Typography>
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.5, mb: 1 }}>
          <LocationOn sx={{ fontSize: 16, color: 'text.secondary' }} />
          <Typography variant="body2" color="text.secondary" noWrap>{property.address}, {property.city}</Typography>
        </Stack>
        {property.nearby_university && (
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1 }}>
            <School sx={{ fontSize: 16, color: 'primary.main' }} />
            <Typography variant="body2" color="primary" noWrap>{property.nearby_university}</Typography>
          </Stack>
        )}
        <Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: 'wrap', gap: 0.5 }}>
          {property.has_wifi && <Chip icon={<Wifi />} label="WiFi" size="small" variant="outlined" />}
          {property.has_electricity && <Chip icon={<Bolt />} label="Power" size="small" variant="outlined" />}
          {property.has_furnished && <Chip label="Furnished" size="small" variant="outlined" />}
        </Stack>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            {property.price_per_month && (
              <Typography variant="h6" fontWeight={700} color="primary">
                {property.currency} {property.price_per_month?.toLocaleString()}<Typography component="span" variant="body2" color="text.secondary">/mo</Typography>
              </Typography>
            )}
            {!property.price_per_month && property.price_per_semester && (
              <Typography variant="h6" fontWeight={700} color="primary">
                {property.currency} {property.price_per_semester?.toLocaleString()}<Typography component="span" variant="body2" color="text.secondary">/sem</Typography>
              </Typography>
            )}
          </Box>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Star sx={{ fontSize: 16, color: '#ffc107' }} />
            <Typography variant="body2" fontWeight={600}>{property.rating?.toFixed(1)}</Typography>
          </Stack>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {property.available_rooms} room{property.available_rooms !== 1 ? 's' : ''} available &bull; {property.room_type}
        </Typography>
      </CardContent>
    </Card>
  );
}

function PropertyListPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filterOpen, setFilterOpen] = useState(false);
  const [filters, setFilters] = useState({
    query: searchParams.get('query') || '',
    city: searchParams.get('city') || '', university: searchParams.get('university') || '',
    property_type: '', location_type: searchParams.get('location_type') || '',
    room_type: '', min_price: '', max_price: '',
    allows_pairing: false, has_wifi: false, has_furnished: false,
    has_security: false, has_parking: false, has_ac: false,
    has_generator: false, has_laundry: false, has_kitchen: false,
    has_electricity: false, bills_included: false, has_washing_machine: false,
    gender_preference: '',
    latitude: '', longitude: '', radius_km: 5,
    sort_by: 'created_at', sort_order: 'desc',
  });
  const [nearMeLoading, setNearMeLoading] = useState(false);
  const [universities, setUniversities] = useState([]);

  useEffect(() => {
    const fetchUniversities = async () => {
      try {
        const res = await ghanaAPI.getUniversities();
        setUniversities(res.data.universities || []);
      } catch (_) {}
    };
    fetchUniversities();
  }, []);

  const fetchProperties = async () => {
    setLoading(true);
    try {
      const params = { page, per_page: 12 };
      Object.entries(filters).forEach(([key, val]) => {
        if (val !== '' && val !== false) params[key] = val;
      });
      const res = await propertiesAPI.list(params);
      setProperties(res.data.properties);
      setTotal(res.data.total);
    } catch (err) {
      console.error('Failed to fetch properties:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchProperties(); }, [page]);

  const handleSearch = () => { setPage(1); fetchProperties(); };

  const filterPanel = (
    <Box sx={{ p: 2, width: isMobile ? 300 : '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" fontWeight={600}>Filters</Typography>
        {isMobile && <IconButton onClick={() => setFilterOpen(false)}><Close /></IconButton>}
      </Box>
      <FormControl fullWidth sx={{ mb: 2 }} size="small">
        <InputLabel>Property Type</InputLabel>
        <Select value={filters.property_type} label="Property Type" onChange={(e) => setFilters({ ...filters, property_type: e.target.value })}>
          <MenuItem value="">All</MenuItem>
          <MenuItem value="hostel">Hostel</MenuItem>
          <MenuItem value="apartment">Apartment</MenuItem>
          <MenuItem value="shared_room">Shared Room</MenuItem>
          <MenuItem value="single_room">Single Room</MenuItem>
          <MenuItem value="self_contained">Self Contained</MenuItem>
          <MenuItem value="flat">Flat</MenuItem>
        </Select>
      </FormControl>
      <FormControl fullWidth sx={{ mb: 2 }} size="small">
        <InputLabel>Location</InputLabel>
        <Select value={filters.location_type} label="Location" onChange={(e) => setFilters({ ...filters, location_type: e.target.value })}>
          <MenuItem value="">All</MenuItem>
          <MenuItem value="on_campus">On Campus</MenuItem>
          <MenuItem value="off_campus">Off Campus</MenuItem>
        </Select>
      </FormControl>
      <FormControl fullWidth sx={{ mb: 2 }} size="small">
        <InputLabel>Room Type</InputLabel>
        <Select value={filters.room_type} label="Room Type" onChange={(e) => setFilters({ ...filters, room_type: e.target.value })}>
          <MenuItem value="">All</MenuItem>
          <MenuItem value="single">Single</MenuItem>
          <MenuItem value="double">Double</MenuItem>
          <MenuItem value="triple">Triple</MenuItem>
          <MenuItem value="quad">Quad</MenuItem>
        </Select>
      </FormControl>
      <FormControl fullWidth sx={{ mb: 2 }} size="small">
        <InputLabel>University</InputLabel>
        <Select value={filters.university} label="University" onChange={(e) => setFilters({ ...filters, university: e.target.value })}>
          <MenuItem value="">All Universities</MenuItem>
          {universities.map((u) => (
            <MenuItem key={u.abbreviation} value={u.name}>{u.name} ({u.abbreviation})</MenuItem>
          ))}
        </Select>
      </FormControl>
      <Grid container spacing={1} sx={{ mb: 2 }}>
        <Grid item xs={6}>
          <TextField fullWidth size="small" label="Min Price" type="number" value={filters.min_price} onChange={(e) => setFilters({ ...filters, min_price: e.target.value })} onKeyDown={decimalOnly} />
        </Grid>
        <Grid item xs={6}>
          <TextField fullWidth size="small" label="Max Price" type="number" value={filters.max_price} onChange={(e) => setFilters({ ...filters, max_price: e.target.value })} onKeyDown={decimalOnly} />
        </Grid>
      </Grid>
      <TextField fullWidth size="small" label="City" value={filters.city} onChange={(e) => setFilters({ ...filters, city: e.target.value })} sx={{ mb: 2 }} />

      <FormControl fullWidth sx={{ mb: 2 }} size="small">
        <InputLabel>Gender Preference</InputLabel>
        <Select value={filters.gender_preference} label="Gender Preference" onChange={(e) => setFilters({ ...filters, gender_preference: e.target.value })}>
          <MenuItem value="">Any</MenuItem>
          <MenuItem value="male">Male Only</MenuItem>
          <MenuItem value="female">Female Only</MenuItem>
        </Select>
      </FormControl>

      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Amenities</Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0 }}>
        <FormControlLabel control={<Checkbox size="small" checked={filters.has_wifi} onChange={(e) => setFilters({ ...filters, has_wifi: e.target.checked })} />} label={<Typography variant="body2">WiFi</Typography>} />
        <FormControlLabel control={<Checkbox size="small" checked={filters.has_electricity} onChange={(e) => setFilters({ ...filters, has_electricity: e.target.checked })} />} label={<Typography variant="body2">Electricity</Typography>} />
        <FormControlLabel control={<Checkbox size="small" checked={filters.has_furnished} onChange={(e) => setFilters({ ...filters, has_furnished: e.target.checked })} />} label={<Typography variant="body2">Furnished</Typography>} />
        <FormControlLabel control={<Checkbox size="small" checked={filters.has_security} onChange={(e) => setFilters({ ...filters, has_security: e.target.checked })} />} label={<Typography variant="body2">Security</Typography>} />
        <FormControlLabel control={<Checkbox size="small" checked={filters.has_parking} onChange={(e) => setFilters({ ...filters, has_parking: e.target.checked })} />} label={<Typography variant="body2">Parking</Typography>} />
        <FormControlLabel control={<Checkbox size="small" checked={filters.has_ac} onChange={(e) => setFilters({ ...filters, has_ac: e.target.checked })} />} label={<Typography variant="body2">AC</Typography>} />
        <FormControlLabel control={<Checkbox size="small" checked={filters.has_generator} onChange={(e) => setFilters({ ...filters, has_generator: e.target.checked })} />} label={<Typography variant="body2">Generator</Typography>} />
        <FormControlLabel control={<Checkbox size="small" checked={filters.has_laundry} onChange={(e) => setFilters({ ...filters, has_laundry: e.target.checked })} />} label={<Typography variant="body2">Laundry</Typography>} />
        <FormControlLabel control={<Checkbox size="small" checked={filters.has_kitchen} onChange={(e) => setFilters({ ...filters, has_kitchen: e.target.checked })} />} label={<Typography variant="body2">Kitchen</Typography>} />
        <FormControlLabel control={<Checkbox size="small" checked={filters.has_washing_machine} onChange={(e) => setFilters({ ...filters, has_washing_machine: e.target.checked })} />} label={<Typography variant="body2">Washing Machine</Typography>} />
        <FormControlLabel control={<Checkbox size="small" checked={filters.bills_included} onChange={(e) => setFilters({ ...filters, bills_included: e.target.checked })} />} label={<Typography variant="body2">Bills Included</Typography>} />
        <FormControlLabel control={<Checkbox size="small" checked={filters.allows_pairing} onChange={(e) => setFilters({ ...filters, allows_pairing: e.target.checked })} />} label={<Typography variant="body2">Room Pairing</Typography>} />
      </Box>

      <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 2, mb: 1 }}>Near Me</Typography>
      <Button
        fullWidth variant="outlined" size="small" startIcon={<MyLocation />}
        disabled={nearMeLoading}
        onClick={() => {
          setNearMeLoading(true);
          navigator.geolocation.getCurrentPosition(
            (pos) => {
              setFilters({ ...filters, latitude: pos.coords.latitude, longitude: pos.coords.longitude });
              setNearMeLoading(false);
            },
            () => { setNearMeLoading(false); alert('Could not get your location'); },
            { enableHighAccuracy: true }
          );
        }}
        sx={{ mb: 1 }}
      >
        {nearMeLoading ? 'Getting location...' : filters.latitude ? 'Location set' : 'Use My Location'}
      </Button>
      {filters.latitude && (
        <Box sx={{ mb: 1 }}>
          <Typography variant="caption" color="text.secondary">Radius: {filters.radius_km} km</Typography>
          <Slider size="small" min={1} max={50} value={filters.radius_km} onChange={(e, v) => setFilters({ ...filters, radius_km: v })} />
        </Box>
      )}

      <Button fullWidth variant="contained" onClick={handleSearch} sx={{ mt: 2 }}>Apply Filters</Button>
      <Button fullWidth variant="text" onClick={() => {
        setFilters({ query: '', city: '', university: '', property_type: '', location_type: '', room_type: '', min_price: '', max_price: '', allows_pairing: false, has_wifi: false, has_furnished: false, has_security: false, has_parking: false, has_ac: false, has_generator: false, has_laundry: false, has_kitchen: false, has_electricity: false, bills_included: false, has_washing_machine: false, gender_preference: '', latitude: '', longitude: '', radius_km: 5, sort_by: 'created_at', sort_order: 'desc' });
      }} sx={{ mt: 1 }}>Clear Filters</Button>
    </Box>
  );

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Typography variant="h4" fontWeight={700} gutterBottom>Find Accommodation</Typography>
      <Paper sx={{ p: 1.5, mb: 3, display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
        <TextField
          fullWidth placeholder="Search by name, location, university..."
          value={filters.query} onChange={(e) => setFilters({ ...filters, query: e.target.value })}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment>, sx: { '& fieldset': { border: 'none' } } }}
        />
        {isMobile && <Button startIcon={<FilterList />} variant="outlined" onClick={() => setFilterOpen(true)}>Filters</Button>}
        <Button variant="contained" onClick={handleSearch} sx={{ minWidth: 120 }}>Search</Button>
      </Paper>

      <Grid container spacing={3}>
        {!isMobile && (
          <Grid item xs={12} md={3}>
            <Paper sx={{ borderRadius: 3 }}>{filterPanel}</Paper>
          </Grid>
        )}
        <Grid item xs={12} md={isMobile ? 12 : 9}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="body2" color="text.secondary">{total} properties found</Typography>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <Select value={`${filters.sort_by}-${filters.sort_order}`} onChange={(e) => {
                const [sb, so] = e.target.value.split('-');
                setFilters({ ...filters, sort_by: sb, sort_order: so });
              }}>
                <MenuItem value="created_at-desc">Newest First</MenuItem>
                <MenuItem value="created_at-asc">Oldest First</MenuItem>
                <MenuItem value="price_per_month-asc">Price: Low to High</MenuItem>
                <MenuItem value="price_per_month-desc">Price: High to Low</MenuItem>
                <MenuItem value="rating-desc">Highest Rated</MenuItem>
                {filters.latitude && <MenuItem value="distance-asc">Nearest First</MenuItem>}
              </Select>
            </FormControl>
          </Box>

          {loading ? (
            <Grid container spacing={3}>
              {[...Array(6)].map((_, i) => (
                <Grid item xs={12} sm={6} lg={4} key={i}>
                  <Card><Skeleton variant="rectangular" height={200} /><CardContent><Skeleton /><Skeleton width="60%" /></CardContent></Card>
                </Grid>
              ))}
            </Grid>
          ) : properties.length === 0 ? (
            <Paper sx={{ p: 6, textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary">No properties found</Typography>
              <Typography variant="body2" color="text.secondary">Try adjusting your filters</Typography>
            </Paper>
          ) : (
            <>
              <Grid container spacing={3}>
                {properties.map((prop) => (
                  <Grid item xs={12} sm={6} lg={4} key={prop.id}>
                    <PropertyCard property={prop} />
                  </Grid>
                ))}
              </Grid>
              {total > 12 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                  <Pagination count={Math.ceil(total / 12)} page={page} onChange={(e, v) => setPage(v)} color="primary" />
                </Box>
              )}
            </>
          )}
        </Grid>
      </Grid>

      {isMobile && <Drawer anchor="right" open={filterOpen} onClose={() => setFilterOpen(false)}>{filterPanel}</Drawer>}
    </Container>
  );
}

export default PropertyListPage;
